import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonDataScience = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Intro to Data Science: Numpy & Pandas</h1>
                <p className="subtitle">The engine behind the AI revolution. Understanding how Python handles Big Data.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea: Why Python?</h2>
                <ProTip><p>
                        Let's be honest. Python is slow. In the world of C++ or Rust, Python is a turtle. It processes data line-by-line, dynamically checking types ("Is this an integer? Is this a string?") at every single step. For a list of 1,000 items, this is fine. For a dataset of <strong>100 million rows</strong>, this is catastrophic.
                    </p>
                    <p>
                        So why is Python the #1 language for Data Science and AI?
                    </p>
                    <p>
                        Python uses a clever trick. We write code in friendly, high-level Python. But underneath, libraries like <strong>Numpy</strong> and <strong>Pandas</strong> hand off the heavy lifting to incredibly optimized <strong>C and Fortran</strong> code.
                    </p>
                    <p>
                        <strong>Numpy</strong> is not just "a list of numbers". It is a contiguous block of memory, exactly like a C array. It bypasses Python's slowness entirely, allowing you to crunch gigabytes of data in milliseconds.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>2. Numpy: The Foundation of Everything</h2>
                <p>
                    Numpy (Numerical Python) is the bedrock. Pandas is built on Numpy. PyTorch is built on Numpy. Scikit-Learn is built on Numpy. If you understand Numpy, you understand the entire ecosystem.
                </p>

                <h3>2.1 The N-Dimensional Array (`ndarray`)</h3>
                <p>
                    The `ndarray` is the core object. Unlike a Python List, which is a collection of pointers to objects scattered in memory (Linked List style), an `ndarray` is a <strong>single solid block of memory</strong>.
                </p>
                <CodeBlock language="python" code={`import numpy as np
import sys
import { ProTip } from '../../components/LessonUI';

# A standard Python List
py_list = [1, 2, 3, 4, 5]
# Each integer object in Python is ~28 bytes. The list itself is extra overhead.

# A Numpy Array
np_arr = np.array([1, 2, 3, 4, 5], dtype='int32')
# Each integer is EXACTLY 4 bytes (32 bits).
# 5 * 4 = 20 bytes for the whole dataset.

print(f"Item size: {np_arr.itemsize} bytes") # Output: 4`} />

                <h3>2.2 The Power of Vectorization</h3>
                <p>
                    This is the single most important concept in Data Science programming. <strong>Vectorization</strong> means applying an operation to an entire array at once, rather than looping through elements one by one.
                </p>
                <p>
                    When you loop in Python, the interpreter has to decode the instruction 1,000,000 times. When you vectorize, Numpy pushes the loop down into C, where the CPU can use SIMD (Single Instruction, Multiple Data) instructions to process 4, 8, or 16 numbers per clock cycle.
                </p>
                <CodeBlock language="python" code={`import time

# Create a massive dataset: 10 Million numbers
size = 10_000_000
data_list = list(range(size))
data_arr = np.arange(size)

# --- Python For Loop ---
start = time.time()
result_list = [x + 5 for x in data_list]
end = time.time()
print(f"Python Loop: {end - start:.4f} seconds")
# Result: ~1.2 seconds

# --- Numpy Vectorization ---
start = time.time()
result_arr = data_arr + 5  # <--- THE MAGIC
end = time.time()
print(f"Numpy Vector: {end - start:.4f} seconds")
# Result: ~0.015 seconds
# SPEEDUP: ~80x FASTER`} />
            </div>

            <div className="content-section">
                <h2>3. Advanced Numpy Mechanics</h2>

                <h3>3.1 Broadcasting</h3>
                <p>
                    Broadcasting describes how Numpy treats arrays with different shapes during arithmetic operations. It "stretches" the smaller array to match the larger one without actually copying data.
                </p>
                <p>
                    <strong>Rule:</strong> Two dimensions are compatible when:
                    <ol>
                        <li>They are equal, or</li>
                        <li>One of them is 1.</li>
                    </ol>
                </p>
                <CodeBlock language="python" code={`# Matrix (3x3)
A = np.array([
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9]
])

# Vector (1x3)
B = np.array([10, 20, 30])

# Numpy "stacks" B three times vertically to match A
C = A + B

# Row 1: [1, 2, 3] + [10, 20, 30] = [11, 22, 33]
# Row 2: [4, 5, 6] + [10, 20, 30] = [14, 25, 36]
# Row 3: [7, 8, 9] + [10, 20, 30] = [17, 28, 39]`} />

                <h3>3.2 Boolean Masking</h3>
                <p>
                    Instead of writing `if` statements inside loops, passing a boolean condition to an array returns a new array of only the elements where the condition is True.
                </p>
                <CodeBlock language="python" code={`scores = np.array([55, 89, 76, 32, 99, 41])

# Who passed? (Score &gt; 50)
print(scores &gt; 50)
# Output: [True, True, True, False, True, False]

# Get the actual scores
passing_scores = scores[scores &gt; 50]
print(passing_scores)
# Output: [55, 89, 76, 99]`} />
            </div>

            <div className="content-section">
                <h2>4. Pandas: Excel for Programmers</h2>
                <p>
                    While Numpy handles numbers, <strong>Pandas</strong> handles <strong>Data</strong>. Real-world data is messy. It has labels, missing values, timestamps, and mixed types (strings and numbers side-by-side).
                </p>
                <p>
                    The core structure is the <strong>DataFrame</strong>. Think of it as a programmable Excel spreadsheet.
                    <ul>
                        <li><strong>Columns:</strong> Each column is a Pandas Series (built on Numpy).</li>
                        <li><strong>Index:</strong> The labels for the rows (can be integers or even timestamps).</li>
                    </ul>
                </p>

                <h3>4.1 Loading and Inspecting</h3>
                <CodeBlock language="python" code={`import pandas as pd

# Pandas can read almost ANY format:
# .read_csv(), .read_excel(), .read_json(), .read_sql(), .read_parquet()
df = pd.read_csv("sales_data.csv")

# Quick Inspection
print(df.head())      # First 5 rows
print(df.shape)       # (Rows, Columns) e.g., (1000, 5)
print(df.info())      # Data types and missing values
print(df.describe())  # Statistical summary (Mean, Min, Max, std deviation)`} />
            </div>

            <div className="content-section">
                <h2>5. Essential Pandas Operations</h2>

                <h3>5.1 Selecting Data: `loc` vs `iloc`</h3>
                <p>
                    This is the #1 confusion for beginners.
                </p>
                <ul>
                    <li><strong>`loc[row_label, col_label]`:</strong> Selection by <strong>Name</strong>. "Give me the row labeled '2023-01-01'".</li>
                    <li><strong>`iloc[row_pos, col_pos]`:</strong> Selection by <strong>Integer Position</strong>. "Give me the 5th row".</li>
                </ul>
                <CodeBlock language="python" code={`# Get the 'Price' of the row with Index 5
price = df.loc[5, "Price"]

# Get the first 10 rows and the first 3 columns
subset = df.iloc[0:10, 0:3]`} />

                <h3>5.2 GroupBy: Split-Apply-Combine</h3>
                <p>
                    This pattern allows you to segment your data.
                    <ol>
                        <li><strong>Split:</strong> Break the data into groups based on a key (e.g., "Category").</li>
                        <li><strong>Apply:</strong> Compute a function for each group (e.g., Sum, Mean, Max).</li>
                        <li><strong>Combine:</strong> Glue the results back into a new DataFrame.</li>
                    </ol>
                </p>
                <CodeBlock language="python" code={`# DATASET:
# Category | Sales
# Fruit    | 100
# Fruit    | 200
# Veg      | 50

# 1. Group by Category
grouped = df.groupby("Category")

# 2. Apply Sum
totals = grouped["Sales"].sum()

# RESULT:
# Fruit: 300
# Veg:   50`} />
            </div>

            <div className="content-section">
                <h2>6. Cleaning and Merging</h2>

                <h3>6.1 Handling Missing Data</h3>
                <p>
                    Real data has holes. Pandas represents missing data as `NaN` (Not a Number).
                </p>
                <CodeBlock language="python" code={`# Check for missing values
print(df.isna().sum())

# Option 1: Drop rows with missing data
clean_df = df.dropna()

# Option 2: Fill gaps with a default value (Imputation)
# Fill missing ages with the average age
mean_age = df["Age"].mean()
df["Age"] = df["Age"].fillna(mean_age)`} />

                <h3>6.2 Merging (SQL Joins)</h3>
                <p>
                    You can join two DataFrames just like SQL tables.
                </p>
                <CodeBlock language="python" code={`# Start with two tables: Users and Orders
users = pd.DataFrame({'id': [1, 2], 'name': ['Alice', 'Bob']})
orders = pd.DataFrame({'order_id': [101, 102], 'user_id': [1, 1]})

# INNER JOIN: Only return matches
merged = pd.merge(users, orders, left_on='id', right_on='user_id', how='inner')
# Result will show Alice's orders. Bob (who has no orders) is excluded.`} />
            </div>

        </div>
    );
};

export default PythonDataScience;


